import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useFiltersMeta, prodottoLabel } from "@/components/Filters";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { dateIt, eur, num, statoClass, statoLabel, ND } from "@/lib/format";
import { CheckCircle2, Circle } from "lucide-react";

const F = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-right">{value ?? ND}</span>
  </div>
);

export default function PraticaDetail() {
  const { numero } = useParams();
  const [d, setD] = useState(null);
  const meta = useFiltersMeta();
  useEffect(() => { api.get(`/pratiche/${numero}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [numero]);

  if (d === false) return <Card className="p-8 text-sm text-red-600">Pratica non trovata o accesso non consentito.</Card>;
  if (!d) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const p = d.pratica;

  return (
    <div data-testid="pratica-detail">
      <SectionTitle sub={`Caricata il ${dateIt(p.data_caricamento)} · ${p.canale}`}
        right={<Link to="/pratiche" className="text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">← Torna alle pratiche</Link>}>
        Pratica {p.numero_pratica}
      </SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6" data-testid="sez-dati">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Dati pratica</h3>
          <F label="Numero pratica" value={p.numero_pratica} />
          <F label="Stato sorgente" value={p.stato} />
          <F label="Stato normalizzato" value={<span className={`text-xs border rounded px-2 py-0.5 ${statoClass(p.stato_norm)}`}>{statoLabel(p.stato_norm)}</span>} />
          <F label="Data caricamento" value={dateIt(p.data_caricamento)} />
          <F label="Data approvazione" value={dateIt(p.data_approvazione)} />
          <F label="Data liquidazione" value={dateIt(p.data_liquidazione)} />
          <F label="Cliente" value={p.cliente} />
        </Card>
        <Card className="p-6" data-testid="sez-dealer">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Dealer e commerciale</h3>
          <F label="Codice dealer" value={p.dealer_codice} />
          <F label="Ragione sociale" value={d.dealer ? (
            <Link className="text-blue-600 hover:underline" to={`/convenzionati/${p.dealer_codice}`}>{d.dealer.ragione_sociale}</Link>
          ) : p.dealer_nome} />
          <F label="Commerciale" value={d.commerciale ? (
            <Link className="text-blue-600 hover:underline" to={`/commerciali/${d.commerciale.slug}`}>{d.commerciale.nome_completo}</Link>
          ) : p.commerciale_nome_raw} />
          <F label="Gestione (sorgente)" value={p.gestione_raw} />
          <F label="Venditore" value={p.venditore} />
          <F label="Caricata dal dealer" value={p.caricata_dal_dealer === null ? ND : p.caricata_dal_dealer ? "Sì" : "No"} />
          <F label="Firma digitale" value={p.firma_digitale === null ? ND : p.firma_digitale ? "Sì" : "No"} />
        </Card>
        <Card className="p-6" data-testid="sez-prodotto">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Prodotto e importi</h3>
          <F label="Prodotto" value={prodottoLabel(meta, p.prodotto)} />
          <F label="Settore" value={p.settore} />
          <F label="Categoria macro" value={p.categoria_macro} />
          <F label="Importo finanziato" value={p.importo_finanziato === null ? ND : eur(p.importo_finanziato)} />
        </Card>
        <Card className="p-6" data-testid="sez-assicurazioni">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Assicurazioni</h3>
          <F label="CPI" value={p.cpi === null ? ND : p.cpi ? `Sì · ${p.importo_cpi === null ? ND : eur(p.importo_cpi)}` : "No"} />
          <F label="CVT" value={p.cvt === null ? ND : p.cvt ? `Sì · ${p.importo_cvt === null ? ND : eur(p.importo_cvt)}` : "No"} />
          <F label="Durata CVT" value={p.durata_cvt === null ? ND : `${num(p.durata_cvt)} mesi`} />
          <F label="NFK" value={p.nfk === null ? ND : p.nfk ? `Sì · ${p.importo_nfk === null ? ND : eur(p.importo_nfk)}` : "No"} />
        </Card>
        <Card className="p-6 lg:col-span-2" data-testid="sez-timeline">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-5">Timeline</h3>
          <ol className="space-y-4">
            {d.timeline.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                {t.data ? <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" /> : <Circle size={18} className="text-amber-500 mt-0.5" />}
                <div>
                  <div className="text-sm font-medium">{t.stato}</div>
                  <div className="text-xs text-slate-500 font-mono">{t.data ? dateIt(t.data) : "in corso / data non disponibile"}</div>
                </div>
              </li>
            ))}
          </ol>
          {p.note && <div className="mt-6 text-sm"><span className="text-slate-500">Note: </span>{p.note}</div>}
        </Card>
      </div>
    </div>
  );
}
